<template>
  <div
    data-cy="sidebar"
    class="sidebar"
    :class="{
      'sidebar--open': menuOpen,
      'sidebar--closed': hideSidebar
    }"
    @transitionend="handleTransitionend"
  >
    <div
      v-if="hasSidebar"
      class="sidebar__toggle"
      :class="{ 'sidebar__toggle--hide': hideSidebar }"
    >
      <button
        v-tooltip:bottom-center="
          hideSidebar
            ? $t('components.ecosystems.Navbar.showSidebar')
            : $t('components.ecosystems.Navbar.hideSidebar')
        "
        class="unstyled-button u-padding-2x u-flex u-height-100 u-color-primary-text"
        @click="$store.commit('menus/toggleSidebar')"
      >
        <SvgIcon
          :name="hideSidebar ? 'chevron-right' : 'chevron-left'"
          size="md"
          aria-hidden="true"
        />
      </button>
    </div>

    <div class="sidebar__overlay" @click="hideMenu"></div>
    <aside class="sidebar__content" :class="`u-bg-${bgColor}`">
      <slot></slot>
    </aside>
  </div>
</template>

<script>
import { EventBus } from '@samknows/utils';
import { SvgIcon } from '@samknows/design-system';

export default {
  components: {
    SvgIcon
  },
  props: {
    bgColor: {
      type: String,
      default: 'base-background'
    }
  },
  computed: {
    menuOpen() {
      return this.$store.state.menus.menuOpen;
    },
    hideSidebar() {
      return this.$store.state.menus.hideSidebar;
    },
    hasSidebar() {
      return this.$store.state.menus.hasSidebar;
    }
  },
  watch: {
    menuOpen() {
      this.setBodyScroll();
    }
  },
  mounted() {
    this.$store.commit('menus/hasSidebar', true);
    this.setBodyScroll();
  },
  destroyed() {
    this.$store.commit('menus/hasSidebar', false);
    document.body.style.overflow = '';
  },
  methods: {
    hideMenu() {
      this.$store.commit('menus/toggleMenu', false);
    },
    setBodyScroll() {
      if (this.menuOpen && window.innerWidth < 992) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    },
    handleTransitionend() {
      EventBus.$emit('dashboard-changed');
    }
  }
};
</script>

<style lang="scss" scoped>
$width: 22rem;
$navbar-height: rem-calc(60px);
$sidebar-height: rem-calc(40px);

.sidebar {
  z-index: 1000;
  position: fixed;
  height: calc(100vh - #{$navbar-height});

  transition: margin 0.2s ease-in-out;

  @include breakpoint(large) {
    z-index: 2;
  }

  &__toggle {
    height: $sidebar-height;
    background: color('neutral-background');
    border-right: rem-calc(1px) color(lines) solid;
    border-bottom: rem-calc(1px) color(lines) solid;
    transition: margin 0.2s ease-in-out;
    display: none;

    @include breakpoint(large) {
      display: flex;
      align-items: center;
      justify-content: flex-end;
    }

    [dir='rtl'] & {
      border-right: none;
      border-left: rem-calc(1px) color(lines) solid;
    }

    &--hide {
      border-right: none;
      background: color('base-background');

      [dir='rtl'] & {
        border-left: none;
      }
    }
  }

  &.sidebar--closed {
    margin-left: calc(#{$navbar-height} - #{$width} - 1.25rem);

    border-right: 1px color(lines) solid;

    [dir='rtl'] & {
      margin-right: calc(#{$navbar-height} - #{$width} - 1.25rem);

      border-right: 0;
      border-left: 1px color(lines) solid;
    }
  }

  &__content {
    position: fixed;
    left: -$width;
    top: 0;
    bottom: 0;

    overflow: hidden;
    overflow-y: auto;
    height: 100%;
    width: $width;

    transition: transform 0.2s ease-in-out;

    [dir='rtl'] & {
      left: auto;
      right: -$width;
    }

    @include breakpoint(large) {
      position: static;
      transition: opacity 0.2s ease-out;

      border-right: 1px color(lines) solid;

      [dir='rtl'] & {
        border-right: 0;
        border-left: 1px color(lines) solid;
      }

      .sidebar--closed & {
        opacity: 0;
      }
    }

    .sidebar--open & {
      transform: translateX($width);

      [dir='rtl'] & {
        transform: translateX(-$width);
      }
    }
  }

  &__overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;

    display: none;

    background: rgba(color('overlay'), 0.5);

    .sidebar--open & {
      display: block;
    }
  }
}
</style>

<style lang="scss">
.sidebar__nav-list {
  ul {
    margin: 0;
    padding: 0;

    list-style: none;
  }

  li {
    margin: 0;
    border-bottom: 1px color(lines) solid;
  }

  .li-root {
    margin: 0;
    padding: 15px 20px;

    border-bottom: 1px color(lines) solid;
  }

  .li-root--general {
    border-top: 1px color(lines) solid;
  }

  .li-group {
    padding: 0;
    border-bottom: none;
  }

  a {
    display: block;
    padding: 15px 15px 15px 30px;
    color: inherit;
    color: color(navigation-default-text);
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    text-decoration: none;

    &:hover {
      background: color(navigation-hover-background);
      color: color(navigation-selecting-text);
    }
  }

  a.router-link-active {
    background: color(navigation-selected-background);
    color: color(navigation-selected-text);

    [dir='rtl'] & {
      border-left: 5px solid transparent;
    }
  }

  a:hover {
    text-decoration: none;
  }
}
</style>
